import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
var _dec, _class, _Info;
function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
/** @jsx jsx */
import { Component } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { AccessibleContent, ScreenReaderContent } from '@instructure/ui-a11y-content';
import { Alert } from '@instructure/ui-alerts';
import { Text } from '@instructure/ui-text';
import { ProgressCircle } from '@instructure/ui-progress';
import { Heading } from '@instructure/ui-heading';
import { View } from '@instructure/ui-view';
import { jsx } from '@instructure/emotion';
import t from '@instructure/quiz-i18n/es/format-message';
import { scoreToLetterGrade } from '@instructure/grading-utils';
import { withI18nSupport, withStyleOverrides } from '@instructure/quiz-common';
import FormattedDuration from '../../../shared/FormattedDuration';
import { formatTime } from '../../../../util/formatTimespan';
import generateStyle from './styles';
import generateComponentTheme from './theme';
var unknownPercentageValue = 'UNKNOWN_PERCENTAGE';
export var Info = (_dec = withStyleOverrides(generateStyle, generateComponentTheme), _dec(_class = (_Info = /*#__PURE__*/function (_withI18nSupport) {
  function Info(props) {
    var _this;
    _classCallCheck(this, Info);
    _this = _callSuper(this, Info, [props]);
    _this.progressTextFormatter = _this.progressTextFormatter.bind(_this);
    _this.progressDisplayFormatter = _this.progressDisplayFormatter.bind(_this);
    return _this;
  }
  _inherits(Info, _withI18nSupport);
  return _createClass(Info, [{
    key: "points",
    get: function get() {
      return this.props.quizSessionResult.score;
    }
  }, {
    key: "pointsPresent",
    get: function get() {
      return typeof this.points === 'number';
    }
  }, {
    key: "pointsMissing",
    get: function get() {
      return !this.pointsPresent;
    }
  }, {
    key: "pointsPossible",
    get: function get() {
      return this.props.quizSessionResult.pointsPossible;
    }
  }, {
    key: "pointsPossiblePresent",
    get: function get() {
      return typeof this.pointsPossible === 'number';
    }
  }, {
    key: "pointsPossibleMissing",
    get: function get() {
      return !this.pointsPossiblePresent;
    }
  }, {
    key: "percentageVal",
    get: function get() {
      var ratio = this.props.quizSessionResult.percentage;
      return isNaN(ratio) ? unknownPercentageValue : ratio;
    }
  }, {
    key: "mainPointsText",
    get: function get() {
      if (this.pointsMissing && this.pointsPossibleMissing) {
        return '--';
      }
      var valueToUse = this.pointsPresent ? this.points : this.pointsPossible;
      return t.number(valueToUse);
    }
  }, {
    key: "subPointsText",
    get: function get() {
      if (this.pointsPresent && this.pointsPossiblePresent) {
        return t("Out of {\n        points, plural,\n        one {# point}\n        other {# points}\n      }", {
          points: this.pointsPossible
        });
      }
      if (this.pointsPresent) {
        return t("Earned {points, plural,\n        one {point}\n        other {points}\n      }", {
          points: this.points
        });
      }

      // We use "Points Possible" when only points is present or when nothing is present
      return t("{points, plural,\n      one {Point}\n      other {Points}\n    } possible", {
        points: this.pointsPossible
      });
    }
  }, {
    key: "pointsText",
    get: function get() {
      if (this.pointsPresent && this.pointsPossiblePresent) {
        return t("{points, number} out of {\n        total, plural,\n        one {# point}\n        other {# points}\n      }", {
          points: this.points,
          total: this.pointsPossible
        });
      }
      if (this.pointsPresent) {
        return t("Earned {points, plural,\n        one {# point}\n        other {# points}\n      }", {
          points: this.points
        });
      }

      // We use "Points Possible" when only points is present or when nothing is present
      if (this.pointsPossiblePresent) {
        return t("{points, plural,\n        one {# point}\n        other {# points}\n      } possible", {
          points: this.pointsPossible
        });
      }
      return t('Unknown Score');
    }

    // needed for inst-ui Progress
  }, {
    key: "progressTextFormatter",
    value: function progressTextFormatter(_ref) {
      var valueNow = _ref.valueNow;
      var percentageUnknown = valueNow === unknownPercentageValue;
      return percentageUnknown ? t('Unknown Score') : t('Your score was {percentage}.', {
        percentage: this.formatPercentMax2FractionDigits(valueNow)
      });
    }

    // needed for inst-ui Progress
  }, {
    key: "progressDisplayFormatter",
    value: function progressDisplayFormatter(_ref2) {
      var valueNow = _ref2.valueNow;
      var valueToShow = valueNow === unknownPercentageValue ? t({
        id: 'progress_display',
        "default": '?',
        description: 'placeholder for an unknown percentage value'
      }) : this.formatPercentMax2FractionDigits(valueNow);
      return jsx("div", {
        css: this.props.styles.scorePercentage,
        "data-automation": "sdk-quiz-score-percentage"
      }, valueToShow);
    }
  }, {
    key: "renderAlert",
    value: function renderAlert() {
      var _this$props = this.props,
        quizSessionResult = _this$props.quizSessionResult,
        sessionItemResults = _this$props.sessionItemResults;
      var ungradedCount = sessionItemResults.reduce(function (count, sessionItemResult) {
        return sessionItemResult.getIn(['scoredData', 'gradeStatus']) === 'waiting' ? count + 1 : count;
      }, 0);
      var hasUngradedQuesitons = ungradedCount > 0;
      var alerts = [];
      if (quizSessionResult.isLoaded() && quizSessionResult.autogradingFailed()) {
        var msg = t('There was a grading problem. Please inform your instructor.');
        alerts.push(jsx(Alert, {
          key: "autograding_failed",
          variant: "warning"
        }, msg));
      }
      if (hasUngradedQuesitons && quizSessionResult.isLoaded()) {
        var ungradedText = t("{\n        count, plural,\n        one {# question requires grading}\n        other {# questions require grading}\n      }", {
          count: ungradedCount
        });
        alerts.push(jsx(Alert, {
          key: "manual_grading",
          variant: "info"
        }, ungradedText));
      }
      if (alerts.length) {
        return jsx("div", {
          css: this.props.styles.alertWrapper
        }, jsx("div", {
          css: this.props.styles.alertWidth,
          "data-automation": "sdk-results-requires-grading"
        }, alerts));
      }
    }
  }, {
    key: "renderLetterGrade",
    value: function renderLetterGrade() {
      var letterGrade = scoreToLetterGrade(this.percentageVal * 100, this.props.gradingScheme);
      var gradeText = t('Grade for this attempt');
      var alt = t('Grade for this attempt: { grade }', {
        grade: letterGrade
      });
      return jsx(AccessibleContent, {
        alt: alt
      }, jsx(View, {
        as: "div"
      }, jsx(Text, {
        size: "xx-large",
        weight: "bold",
        color: "primary"
      }, letterGrade)), jsx(Text, {
        color: "secondary"
      }, gradeText));
    }
  }, {
    key: "renderScoreStats",
    value: function renderScoreStats() {
      return jsx("div", {
        css: this.props.styles.scoreStats
      }, this.renderProgress(), this.renderPointsDisplay());
    }
  }, {
    key: "renderProgress",
    value: function renderProgress() {
      if (this.pointsMissing || this.pointsPossibleMissing) {
        return null;
      }
      return jsx("div", {
        css: this.props.styles.score,
        "data-automation": "sdk-info-score"
      }, jsx(ProgressCircle, {
        shouldAnimateOnMount: true,
        size: "large",
        screenReaderLabel: t('Your Score'),
        formatScreenReaderValue: this.progressTextFormatter,
        renderValue: this.progressDisplayFormatter,
        valueNow: this.percentageVal,
        valueMax: 1
      }));
    }
  }, {
    key: "renderPointsDisplay",
    value: function renderPointsDisplay() {
      return jsx("div", {
        css: this.props.styles.infoWrapper
      }, jsx(AccessibleContent, {
        alt: this.pointsText
      }, jsx(Text, {
        as: "div",
        color: "primary",
        size: "xx-large",
        weight: "bold"
      }, this.mainPointsText), jsx(Text, {
        color: "secondary"
      }, this.subPointsText)));
    }
  }, {
    key: "renderTime",
    value: function renderTime() {
      var attemptText = t('Time for this attempt');
      var alt = t('Time for this attempt: { time }', {
        time: formatTime(this.props.quizSession.elapsedTime)
      });
      return jsx(AccessibleContent, {
        alt: alt
      }, jsx(View, {
        as: "div"
      }, jsx(FormattedDuration, {
        fontSize: "xx-large",
        fontWeight: "bold",
        seconds: this.props.quizSession.elapsedTime
      })), jsx(Text, {
        color: "secondary"
      }, attemptText));
    }
  }, {
    key: "render",
    value: function render() {
      return jsx("div", null, this.renderAlert(), jsx("div", {
        css: this.props.styles.wrapper
      }, jsx("div", {
        css: this.props.styles.info,
        className: "fs-mask"
      }, jsx(ScreenReaderContent, null, jsx(Heading, {
        level: "h2"
      }, t('Assessment Statistics'))), this.props.restrictQuantitativeData ? this.renderLetterGrade() : this.renderScoreStats(), jsx("div", {
        css: this.props.styles.infoWrapper
      }, this.renderTime()))));
    }
  }]);
}(withI18nSupport(Component)), _defineProperty(_Info, "displayName", 'QuizSessionResultInfo'), _defineProperty(_Info, "componentId", "Quizzes".concat(_Info.displayName)), _defineProperty(_Info, "propTypes", {
  gradingScheme: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number
  })),
  quizSession: ImmutablePropTypes.record.isRequired,
  quizSessionResult: ImmutablePropTypes.record.isRequired,
  restrictQuantitativeData: PropTypes.bool,
  sessionItemResults: ImmutablePropTypes.list.isRequired,
  styles: PropTypes.object
}), _defineProperty(_Info, "defaultProps", {
  gradingScheme: [],
  restrictQuantitativeData: false
}), _Info)) || _class);
export default Info;